import React, { useEffect, useRef, useState } from "react";
import type { InputNumberProps } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import { Button, Checkbox, CheckboxProps, Input, InputNumber, Select, Slider, Spin } from "antd";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import * as echarts from "echarts";
import {
    AverageTemperatureHistogram,
    IGetLocationsResModel,
    TemperatureStatResponse
} from "@/util/http/ClimateShift/responseType";
import {
    getLocationsFromServices,
    getMultipleYearsAverageContours,
} from "@/util/http/ClimateShift/index";
import {
    updateCommonErrorMessage,
    updateCommonWarnMessage,
    updateDownloadAllLoading,
    updateSelectedLocationDataForAverage
} from "@/redux/actions/climateShiftAction";
import "./index.less";

// Props: data from parent (TemperatureStatResponse)
interface Props {
    data: TemperatureStatResponse;
    activeKey?: string;
}

const HistogramOfYears = React.forwardRef((props: Props & { activeKey?: string }, ref) => {
    const dispatch = useDispatch();

    // Props
    const { data, activeKey } = props;

    // Redux state
    const selectedLocationData = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationData);
    const country = useSelector((state: RootState) => state.climateShiftReducer.country);
    const selectedLocationDataForAverage = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationDataForAverage);

    // Chart and year state
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<echarts.EChartsType | null>(null);
    const years = data.histogramOfYears.map(y => y.year);
    const defaultYearIdx = years.length > 0 ? years.length - 1 : 0;// 默认选中最新一年
    const [yearIdx, setYearIdx] = useState(defaultYearIdx);
    const selectedYear = years[yearIdx];
    const yearData = data.histogramOfYears[yearIdx];
    const [newAverageContours, setNewAverageContours] = useState<AverageTemperatureHistogram[]>([]);
    const averageName = `Average ${newAverageContours[0]?.startYear}-${newAverageContours[0]?.endYear} for ${selectedLocationDataForAverage?.displayName || ""} (${selectedLocationDataForAverage?.postalCode || ""})`;
    const HistogramForYearlyStatistics = `Temperature Distribution for ${selectedLocationData?.displayName || ""} (${selectedLocationData?.postalCode || ""})_${selectedYear}`;

    // 获取浏览器可视区域宽度（更准确反映电脑屏幕尺寸）
    const getScreenWidth = () => document.documentElement.clientWidth || window.innerWidth;
    const [screenWidth, setScreenWidth] = useState(getScreenWidth());

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(getScreenWidth());
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // 监听 data.histogramOfYears 变化，自动切换到最新一年
    useEffect(() => {
        setYearIdx(defaultYearIdx);
    }, [data.histogramOfYears]);

    useEffect(() => {
        if (!chartRef.current || !yearData) return;
        //const chart = echarts.init(chartRef.current, undefined, { devicePixelRatio: window.devicePixelRatio });
        const chart = echarts.init(chartRef.current);
        chartInstanceRef.current = chart;

        // 屏幕尺寸变化时自适应
        function handleResize() {
            chart.resize();
        }
        window.addEventListener("resize", handleResize);
        // 判断是否为移动端
        const isMobile = screenWidth <= 768;
        const fontSize = isMobile ? 12 : 16;
        const legendFontSize = isMobile ? 10 : 14;
        const axisLabelFontSize = isMobile ? 10 : 12;
        const axisNameFontSize = isMobile ? 11 : 12;

        // 颜色插值函数
        function hexToRgb(hex: string) {
            hex = hex.replace("#", "");
            if (hex.length === 3) {
                hex = hex.split("").map(x => x + x).join("");
            }
            const num = parseInt(hex, 16);
            return [
                (num >> 16) & 255,
                (num >> 8) & 255,
                num & 255
            ];
        }

        function rgbToHex(rgb: number[]) {
            return (
                "#" + rgb.map(x => x.toString(16).padStart(2, "0")).join("")
            );
        }

        function interpolateColor(start: string, end: string, t: number) {
            const s = hexToRgb(start);
            const e = hexToRgb(end);
            const rgb = s.map((v, i) => Math.round(v + (e[i] - v) * t));
            return rgbToHex(rgb);
        }

        const option = {
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "none"
                },
                position: isMobile ? "inside" : undefined,
                confine: isMobile ? true : false,
                // 设置tooltip字体更小
                textStyle: {
                    fontSize: isMobile ? 10 : 14,
                },
                // 自定义tooltip位置为居中（仅移动端）
                // 也可以用position: function(point, params, dom, rect, size) { ... }自定义
                formatter: function (params) {
                    if (Array.isArray(params) && params.length > 0) {
                        // 只显示一次温度
                        const temp = params[0].value[0];
                        const tempStr = Number.isFinite(temp) ? Math.round(temp) : temp;
                        const result = [
                            "Temperature: " + tempStr + "℃"
                        ];
                        // 判断是否为移动端
                        const isMobile = window.innerWidth <= 768;
                        params.forEach(item => {
                            const hour = item.value[1];
                            let name = item.seriesName;
                            if (isMobile && typeof name === "string" && name.length > 60) {
                                // 60字符以上自动换行
                                name = name.replace(/(.{50,60})(\s|$)/g, "$1<br/>");
                            }
                            if (item.seriesType === "line") {
                                result.push(`${name}<br/>Hours: ${hour}`);
                            } else if (item.seriesType === "bar") {
                                result.push(`${name}<br/>Hours: ${hour}`);
                            }
                        });
                        return result.join("<br/>");
                    }
                    return "";
                },
            },
            // No title/subtitle on the page; set only for export
            title: {
                text: `Temperature Distribution for ${selectedLocationData?.displayName} (${selectedLocationData?.postalCode})`,
                left: "center",
                top: 20,
                textStyle: {
                    fontSize: fontSize,
                    width: isMobile ? Math.floor(screenWidth * 0.8) : undefined,
                    overflow: isMobile ? "break" : undefined,
                    lineHeight: isMobile ? 12 : undefined,
                }
            },
            legend: {
                data: newAverageContours.length > 0 ? [averageName] : [],
                top: isMobile ? null : 60,
                right: isMobile ? null : 50,
                bottom: isMobile ? 10 : null,
                left: isMobile ? "center" : null,
                orient: "horizontal",
                itemGap: 20,
                textStyle: {
                    fontSize: legendFontSize,
                    width: isMobile ? Math.floor(screenWidth * 0.8) : undefined,
                    overflow: isMobile ? "break" : undefined,
                    lineHeight: isMobile ? 10 : undefined,
                },
                icon: "path://M 0 0 L 2 0 L 2 0.5 L 0 0.5 Z M 3 0 L 5 0 L 5 0.5 L 3 0.5 ZM 6 0 L 8 0 L 8 0.5 L 6 0.5 Z",
                itemStyle: { color: "#000" }
            },
            grid: { left: isMobile ? 45 : 60, right: 30, top: 150, bottom: 80 },
            xAxis: {
                name: "Temperature (℃)",
                nameLocation: "middle",
                nameGap: 30,
                min: (() => {
                    let min = data.minTemperature;
                    if (newAverageContours && newAverageContours.length > 0) {
                        min = Math.min(min, ...newAverageContours[0].xMids);
                    }
                    min = Math.floor(min - 0.5);
                    return min % 5 === 0 ? min : min - (min % 5) - (min < 0 && min % 5 !== 0 ? 5 : 0);
                })(),
                max: (() => {
                    let max = data.maxTemperature;
                    if (newAverageContours && newAverageContours.length > 0) {
                        max = Math.max(max, ...newAverageContours[0].xMids);
                    }
                    max = Math.ceil(max + 0.5);
                    return max % 5 === 0 ? max : max + (5 - (max % 5));
                })(),
                interval: 5,
                type: "value",
                axisLine: { onZero: true },
                splitLine: { show: false },
                axisLabel: { fontSize: axisLabelFontSize, fontFamily: "Arial", color: "#2F3031" },
                nameTextStyle: { fontSize: axisNameFontSize, fontFamily: "Arial", color: "#2F3031" },
            },
            yAxis: {
                name: "Number of Hours",
                nameLocation: "middle",
                nameRotate: 90,
                nameGap: isMobile ? 35 : 40,
                min: 0,
                max: (() => {
                    const interval = 200;
                    let maxVal = typeof data?.maxCount === "number" ? data.maxCount : 1000;
                    if (newAverageContours && newAverageContours.length > 0) {
                        maxVal = Math.max(maxVal, ...newAverageContours[0].counts);
                    }
                    if (maxVal < interval) {
                        return interval;
                    }
                    const maxTick = Math.ceil(maxVal / interval) * interval;
                    // 如果实际最大值大于最大刻度，则再多显示一个刻度
                    return maxVal > maxTick ? maxTick + interval : maxTick;
                })(),
                interval: 200,
                type: "value",
                axisLine: { onZero: false },
                splitLine: { show: false },
                axisLabel: { fontSize: axisLabelFontSize, fontFamily: "Arial", color: "#2F3031" },
                nameTextStyle: { fontSize: axisNameFontSize, fontFamily: "Arial", color: "#2F3031" },
            },
            series: [
                {
                    name: HistogramForYearlyStatistics,
                    type: "bar",
                    data: yearData.xMids.map((x, i) => {
                        // 2°C以下：蓝色系（#0000ff ~ #00ffff）
                        // 2~15°C：指定颜色
                        // 15~20°C：指定颜色
                        // 20℃以上：红色系（#ff0000 ~ #701F29
                        let color;
                        const tempBarColors2To15 = [
                            "#00ffde", "#00ffb5", "#00ff8c", "#00ff63", "#19ff00", "#42ff00", "#6bff00",
                            "#94ff00", "#bdff00", "#e6ff00", "#fff700", "#ffe200", "#ffce00"
                        ];
                        const tempBarColors15To21 = [
                            "#ffb900", "#ffa500", "#ff9000", "#ff7c00", "#ff6700", "#ff3e00"];
                        if (x < 1.5) {
                            // 蓝色系：#0000ff ~ #00ffff
                            const minT = Math.min(...yearData.xMids, 2);
                            const t = (x - minT) / (2 - minT || 1);
                            const clampedT = Math.max(0, Math.min(1, t));
                            color = interpolateColor("#0000ff", "#00ffff", clampedT);
                        } else if (x >= 1.5 && x <= 14.5) {
                            // 2~15°C区间依次指定13个颜色
                            const idx = Math.round(x) - 2;
                            color = tempBarColors2To15[idx] || "#ff3d00";
                        } else if (x > 14.5 && x < 21) {
                            // 15~21°C区间依次指定6个颜色
                            const idx = Math.round(x) - 15;
                            color = tempBarColors15To21[idx] || "#701F29";
                        }
                        else {
                            // 红色系：#ff0000 ~ #B22222（火砖红）
                            const maxT = Math.max(...yearData.xMids, 15);
                            const t = (x - 15) / (maxT - 15 || 1);
                            const clampedT = Math.max(0, Math.min(1, t));
                            color = interpolateColor("#ff0000", "#B22222", clampedT);
                        }
                        return {
                            value: [x, yearData.counts[i]],
                            itemStyle: { color }
                        };
                    }),
                    barWidth: "80%",
                    markLine: {
                        symbol: "none",
                        emphasis: {
                            disabled: true // 悬停时不做任何样式改变
                        },
                        data: [
                            {
                                xAxis: 1.5,
                                lineStyle: { type: "dashed", color: "#3B82F6" },
                                label: { formatter: "2℃", color: "#3B82F6" }
                            },
                            {
                                xAxis: 14.5,
                                lineStyle: { type: "dashed", color: "#DC2626" },
                                label: { formatter: "15℃", color: "#DC2626" }
                            }
                        ]
                    },
                },
                {
                    name: averageName,
                    type: "line",
                    data: newAverageContours.length > 0 ? newAverageContours[0].xMids.map((x, i) => {
                        return [x, newAverageContours[0].counts[i]];
                    }) : [],
                    smooth: true,
                    showSymbol: false,
                    lineStyle: {
                        color: "#000",
                        type: "dashed"
                    },
                    label: {
                        show: false // 默认不显示label
                    },
                    emphasis: {
                        focus: "none", // 不高亮其它
                        symbol: "circle", // 悬停时显示圆点
                        symbolSize: 8,
                    },
                },
            ]
        };
        chart.setOption(option);
        setTimeout(() => {
            chart.resize();
        }, 0);
        return () => {
            window.removeEventListener("resize", handleResize);
            chart.dispose();
        };
    }, [yearIdx, data, activeKey, newAverageContours, screenWidth]);

    // 下载图表为图片（仅导出时显示标题和副标题）
    const handleDownloadChart = () => {
        if (!chartRef.current || !yearData) return;
        const chart = echarts.init(chartRef.current);
        chartInstanceRef.current = chart;

        // 构造带标题的option
        // Chart title/subtitle for export only
        const chartTitle = `Temperature Distribution for ${selectedLocationData?.displayName} (${selectedLocationData?.postalCode})`;
        const chartSubtitle = `Year ${selectedYear}`;

        const exportTitle = [
            {
                text: chartTitle,
                left: "center",
                top: 20,
                textStyle: {
                    fontSize: (screenWidth <= 768 ? 12 : 16),
                    width: screenWidth <= 768 ? Math.floor(screenWidth * 0.8) : undefined,
                    overflow: screenWidth <= 768 ? "break" : undefined,
                    lineHeight: screenWidth <= 768 ? 12 : undefined,
                }
            },
            {
                text: chartSubtitle,
                left: "center",
                top: 48,
                textStyle: {
                    fontSize: (screenWidth <= 768 ? 12 : 16) - 4,
                    fontWeight: "normal",
                    color: "#2F3031"
                }
            }
        ];
        const pageTitle = [
            {
                text: chartTitle,
                left: "center",
                top: 20,
                textStyle: {
                    fontSize: (screenWidth <= 768 ? 12 : 16),
                    width: screenWidth <= 768 ? Math.floor(screenWidth * 0.8) : undefined,
                    overflow: screenWidth <= 768 ? "break" : undefined,
                    lineHeight: screenWidth <= 768 ? 12 : undefined,
                }
            },
            {
                text: "",
                left: "center",
                top: 48,
                textStyle: {
                    display: "none",
                }
            }
        ];
        // 临时设置标题
        chart.setOption({ title: exportTitle });
        // 导出图片
        const url = chart.getDataURL({
            type: "png",
            pixelRatio: 2,
            backgroundColor: "#fff"
        });
        // 恢复原option（无标题）
        chart.setOption({ title: pageTitle });
        // 下载
        const link = document.createElement("a");
        link.href = url;
        link.download = HistogramForYearlyStatistics + ".png";
        link.click();
    };

    // 暴露给父组件获取指定年份图片的方法
    React.useImperativeHandle(ref, () => ({
        getChartImage: async (targetYearIdx?: number) => {
            // 切换到指定年份
            const idx = typeof targetYearIdx === "number" ? targetYearIdx : yearIdx;
            if (idx < 0 || idx >= years.length) return null;
            setYearIdx(idx);
            // 等待图表渲染
            await new Promise(resolve => setTimeout(resolve, 300));
            const chart = chartInstanceRef.current;
            if (chart) {
                return chart.getDataURL({
                    type: "png",
                    pixelRatio: 2,
                    backgroundColor: "#fff"
                });
            }
            return null;
        }
    }));

    //Bottom area: Location search area state and handlers
    const [showSearchArea, setShowSearchArea] = useState(false); // 控制右侧区域显示
    const [address, setAddress] = useState("");// Input address value
    const [showResults, setShowResults] = useState(false);// Whether to show the location results list
    const [locationDatas, setLocationDatas] = useState<IGetLocationsResModel[]>([]); // Location search results
    const [searchLocationLoading, setSearchLocationLoading] = useState(false);
    const locationSelectRef = useRef<any>(null);
    const skipBlurClearRef = useRef(false);

    // 核心：标记最新请求的唯一标识（用递增数字最简单）
    const latestRequestIdRef = useRef(0);

    // Handle checkbox change
    const handleCheckboxChange: CheckboxProps["onChange"] = (e) => {
        setShowSearchArea(e.target.checked);
        if (e.target.checked && selectedLocationData) {
            // 自动选中当前location，并设置address
            if (selectedLocationData?.source?.toLowerCase() === "postalcode") {
                setAddress(`${selectedLocationData?.postalCode} - ${selectedLocationData?.placeName}`);
            } else if (selectedLocationData?.source?.toLowerCase() === "placename") {
                setAddress(`${selectedLocationData?.displayName} - ${selectedLocationData?.postalCode}`);
            }
            else {
                setAddress(`${selectedLocationData?.displayName && selectedLocationData?.displayName?.trim() !== "" ? selectedLocationData?.displayName : selectedLocationData?.placeName || ""}`);
            }
            handleSelectLocationData(selectedLocationData);
        } else if (!e.target.checked) {
            // 取消勾选时，清空相关数据
            setNewAverageContours([]);
        }
    };

    // Handle selecting a location from the results list
    const handleSelectLocationData = async (item: IGetLocationsResModel) => {
        dispatch(updateDownloadAllLoading(true));
        try {
            // Update selected location in Redux
            dispatch(updateSelectedLocationDataForAverage(item));
            if (item?.source?.toLowerCase() === "placename") {
                setAddress(`${item?.displayName} - ${item?.postalCode || ""}`);
            }
            else if (item?.source?.toLowerCase() === "postalcode") {
                const tempAddress = `${item?.postalCode} - ${item?.placeName || ""}`;
                setAddress(tempAddress);
            }
            else {
                setAddress(item?.displayName && item?.displayName?.trim() !== "" ? item?.displayName : item?.placeName || "");
            }
            setShowResults(false);
            skipBlurClearRef.current = true;
            locationSelectRef.current?.blur?.();
            // Fetch average contours data for the selected location
            const res = await getMultipleYearsAverageContours({
                longitude: item.lng,
                latitude: item.lat,
                ranges: [10],
            });

            if (res.status !== 200) {
                dispatch(updateCommonErrorMessage("Failed to fetch average contours data"));
            } else if (res.data.length === 0) {
                dispatch(updateCommonErrorMessage("No average contours data found for the selected location"));
            } else if (res.data.length > 0) {
                setNewAverageContours(res.data);
            }
        } catch (error) {
            dispatch(updateCommonErrorMessage("Error fetching average contours data"));
            console.error(error);
        } finally {
            dispatch(updateDownloadAllLoading(false));
        }
    };

    // 简单防抖函数
    function debounce(fn, delay) {
        let timer = null;
        return function (...args) {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                fn.apply(this, args);
            }, delay);
        };
    }
    // 通用搜索函数，供防抖和定位按钮复用
    const searchLocations = async ({ searchText, countryCode }) => {
        // 3. 生成本次请求的唯一标识，并更新"最新标识"
        const currentRequestId = ++latestRequestIdRef.current;
        setSearchLocationLoading(true);
        try {
            const res = await getLocationsFromServices({ searchText, countryCode });
            if (currentRequestId === latestRequestIdRef.current) {
                const filtered = Array.isArray(res.data)
                    ? res.data.filter(item => item.countryCode === countryCode)
                    : [];
                if (!filtered || filtered.length === 0) {
                    setLocationDatas([]);
                    setShowResults(false);
                    dispatch(updateCommonWarnMessage("No results found for the entered Location / Postal Code."));
                    setAddress(undefined);
                } else if (filtered.length === 1) {
                    setLocationDatas([filtered[0]]);
                    handleSelectLocationData(filtered[0]);
                } else {
                    setLocationDatas(filtered);
                    setShowResults(true);
                }
            }
            else {
                console.log(`request ${currentRequestId} Expired, ignore results`);
            }

        } catch (error) {
            dispatch(updateCommonErrorMessage("An error occurred. Please try again later."));
            console.error("Error fetching locations:", error);
        } finally {
            if (currentRequestId === latestRequestIdRef.current) {
                setSearchLocationLoading(false);
            }

        }
    };
    const debouncedFetchLocations = React.useCallback(
        debounce(async (val) => {
            await searchLocations({ searchText: val, countryCode: country });
        }, 1000),
        [country]
    );

    return (
        <div className="histogram-of-years-container">
            <div className="histogram-chart">
                <div className="download-img" >
                    <Button className="download-btn download-btn-icon-right tooltip-btn"
                        title="Download"
                        onClick={handleDownloadChart}>
                        <span className="download-btn-icon"><DownloadOutlined /></span>
                    </Button>
                </div>
                <div className="slider-div">
                    <span style={{ marginRight: 8, whiteSpace: "nowrap", fontSize: "14px" }}>Year:</span>
                    <Slider
                        min={0}
                        max={years.length - 1}
                        value={yearIdx}
                        onChange={setYearIdx}
                        step={1}
                        style={{ flex: 1, minWidth: 120, maxWidth: 300 }}
                        tooltip={{ open: false }}
                    />
                    <InputNumber
                        style={{ marginLeft: 8, width: 80, fontSize: "14px" }}
                        value={selectedYear}
                        min={Math.min(...years)}
                        max={Math.max(...years)}
                        step={1}
                        onChange={val => {
                            if (typeof val !== "number" || isNaN(val)) return;
                            const idx = years.findIndex(y => y === val);
                            if (idx !== -1) setYearIdx(idx);
                        }}
                    />
                </div>
                <div ref={chartRef} className="histogram-chart-responsive" />
            </div>
            {/* <Spin spinning={searchLocationLoading}> */}
            <div className="bottom-search-area-row">
                <div className="bottom-search-area-row-left">
                    <Checkbox checked={showSearchArea} onChange={handleCheckboxChange}>Show Average</Checkbox>
                </div>
                {showSearchArea && (
                    <div className="bottom-search-area-row-right">
                        <div className="search-label">Location for Temperature Average</div>
                        <div className="search-input-row">
                            <Spin spinning={searchLocationLoading}>
                                <Select
                                    ref={locationSelectRef}
                                    size="large"
                                    showSearch
                                    value={address}
                                    placeholder="Enter Location or Postal Code"
                                    style={{ width: "100%" }}
                                    filterOption={false}
                                    notFoundContent={searchLocationLoading ? <Spin size="small" /> : null}
                                    onSearch={val => {
                                        setShowResults(false);
                                        const minLen = country === "GB" ? 2 : 3;
                                        if (val && val.length >= minLen) {
                                            debouncedFetchLocations(val);
                                        }
                                    }}
                                    onFocus={() => {
                                        setAddress(undefined);
                                        setShowResults(false);
                                        dispatch(updateSelectedLocationDataForAverage(null));
                                    }}
                                    onChange={val => {
                                        setAddress(val);
                                        setShowResults(false);
                                        setLocationDatas([]);
                                        dispatch(updateSelectedLocationDataForAverage(null)); // Clear selected location for average in Redux

                                        // 选中下拉项时自动更新location数据
                                        const selected = locationDatas.find(item => {
                                            let value = "";
                                            if (item?.source?.toLowerCase() === "placename") {
                                                value = `${item?.displayName} - ${item?.postalCode || ""}`;
                                            } else if (item?.source?.toLowerCase() === "postalcode") {
                                                value = `${item?.postalCode} - ${item?.placeName || ""}`;
                                            } else {
                                                value = item?.displayName || item?.placeName || item?.postalCode;
                                            }
                                            return value === val;
                                        });
                                        if (selected) {
                                            handleSelectLocationData(selected);
                                        }
                                    }}
                                    allowClear
                                >
                                    {showResults && locationDatas && locationDatas.length > 0 && locationDatas.map((item, idx) => {
                                        let value = "";
                                        if (item?.source?.toLowerCase() === "placename") {
                                            value = `${item?.displayName} - ${item?.postalCode || ""}`;
                                        } else if (item?.source?.toLowerCase() === "postalcode") {
                                            value = `${item?.postalCode} - ${item?.placeName || ""}`;
                                        } else {
                                            value = item?.displayName || item?.placeName || item?.postalCode;
                                        }
                                        return (
                                            <Select.Option key={value + idx} value={value}>
                                                {item?.source?.toLowerCase() === "placename"
                                                    ? `${item?.displayName} - ${item?.postalCode}`
                                                    : item?.source?.toLowerCase() === "postalcode"
                                                        ? `${item?.postalCode} - ${item?.placeName}`
                                                        : `${item?.displayName}`}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </Spin>
                        </div>
                        {/* {showResults && locationDatas && locationDatas.length > 0 && (
                                <ul
                                    className="location-results-list up"
                                >
                                    {locationDatas.map((item, idx) => (
                                        <li
                                            key={(item?.placeName || "") + idx}
                                            className="location-result-item"
                                            onClick={() => handleSelectLocationData(item)}
                                        >
                                            {item?.source?.toLowerCase() === "placename"
                                                ? `${item?.displayName} - ${item?.postalCode}`
                                                : item?.source?.toLowerCase() === "postalcode"
                                                    ? `${item?.postalCode} - ${item?.placeName}`
                                                    : `${item?.displayName}`}
                                        </li>
                                    ))}
                                </ul>
                            )} */}
                    </div>
                )}
            </div>
            {/* </Spin> */}
        </div>
    );
});

HistogramOfYears.displayName = "HistogramOfYears";
export default HistogramOfYears;
